import LipSyncPreviewImg from '@/shared/assets/images/lip-sync-preview.jpg';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';

import { LimitedFeatureContent, ModalContent } from '../types';
import { getBullets } from './bullets';
import { BULLETS_HEADER_TEXT, BUTTON_TEXTS } from './texts';

const LIP_SYNC_DEMO_VIDEO_SRC = 'https://static.rask.ai/MLlips.mp4';

const BASE_CONTENT: ModalContent = {
  title: 'Unlock Lip-sync feature',
  bulletsHeader: BULLETS_HEADER_TEXT.withCreatorPro,
  buttonText: BUTTON_TEXTS.toCreatorPro,
  features: getBullets([
    'translate100',
    'lipSync',
    'voiceCloneSettings',
    'voicePresets',
    'srtUploadDownload',
    'subtitles',
    'aiRewrite',
  ]),
  media: {
    poster: LipSyncPreviewImg,
    src: LIP_SYNC_DEMO_VIDEO_SRC,
    type: 'video',
    videoOptions: {
      autoPlay: true,
      controls: true,
      loop: true,
    },
  },
  mixpanelButtonClickEvent: MixpanelEvents.lipsyncUpgradeChoosePlanModalClick,
  mixpanelOpeningEvent: MixpanelEvents.lipsyncUpgradeModalShown,
  subtitle: 'Upgrade to Creator Pro plan to add lip-sync to your videos and experience top-notch translation quality',
};

const TRIAL_PLAN_CONTENT = {
  ...BASE_CONTENT,
  buttonText: 'Unlock Lip-sync',
  content: {
    allowedPlanNames: ['Creator Pro', 'Business'],
    chip: {
      title: 'Get professional content with Lip-sync and other Pro tools',
    },
    learnMore: {
      title: 'Learn more',
      link: 'https://help.rask.ai/hc/introducing-the-new-lip-sync-model-with-better-quality-rask-help-center',
      text: 'about Lip-sync feature',
    },
    perfectFor: {
      title: 'Perfect for',
      text: 'professional content where visual authenticity matters.',
    },
    selectedPlan: 'Creator Pro',
    whatYouCanDo: {
      title: 'What you’ll get',
      bullets: [
        'Synchronized lip movements with translated audio',
        'Natural-looking dubbed videos in any language',
        'Works with multiple speakers and faces',
      ],
    },
  },
  features: [],
  subtitle: 'Make dubbed videos look natural by matching mouth movements to translated audio.',
};

const FALLBACK_ENHANCED_LIP_SYNC_MULTIPLIER = 3;

const getTrialPlanContentEnhanced = (multiplier: number) => ({
  ...TRIAL_PLAN_CONTENT,
  content: {
    ...TRIAL_PLAN_CONTENT.content,
    allowedPlanNames: ['Creator Pro', 'Business'],
    whatYouCanDo: {
      title: 'What you’ll get',
      bullets: [
        `New Enhanced lip-sync model — studio-grade quality (deducts ${multiplier} minutes per 1 minute of video)`,
        'Synchronized lip movements with translated audio for multiple speakers',
        'Natural-looking dubbed videos in any language',
      ],
    },
  },
  subtitle:
    'Make dubbed videos look natural by matching mouth movements to translated audio. Available on Creator Pro and above.',
});

const STARTER_PLAN_CONTENT = {
  ...BASE_CONTENT,
  bulletsHeader: BULLETS_HEADER_TEXT.inTrial,
};

const CREATOR_PLAN_CONTENT = {
  ...BASE_CONTENT,
  bulletsHeader: BULLETS_HEADER_TEXT.inCreator,
};

export const LIP_SYNC_CONTENT: LimitedFeatureContent = {
  creator: CREATOR_PLAN_CONTENT,
  starter: STARTER_PLAN_CONTENT,
  trial: TRIAL_PLAN_CONTENT,
};

export const getLipSyncContentEnhanced = (multiplier?: number): LimitedFeatureContent => ({
  creator: CREATOR_PLAN_CONTENT,
  starter: STARTER_PLAN_CONTENT,
  trial: getTrialPlanContentEnhanced(
    typeof multiplier === 'number' && multiplier > 0 ? multiplier : FALLBACK_ENHANCED_LIP_SYNC_MULTIPLIER,
  ),
});
